
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowRight, Search, Users, LayoutDashboard, ListTodo, Zap, Globe, ShieldCheck, BarChart3, Star } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    { num: '01', title: 'Discover Qualified Leads', desc: 'Use our powerful search engine to find businesses based on location, industry, size, and more. Filter results to match your ideal customer profile.' },
    { num: '02', title: 'Enrich Lead Data', desc: 'Our system automatically enriches each lead with business details, contact information, social profiles, and engagement signals.' },
    { num: '03', title: 'Prioritize & Manage', desc: 'Leads are automatically scored based on fit and intent signals. Organize and track them with our intuitive CRM interface.' },
    { num: '04', title: 'Collaborate & Convert', desc: 'Assign leads, track activities, manage tasks, and collaborate with your team to convert prospects into customers.' },
  ];

  const features = [
    { icon: Search, title: 'Location-Based Search', desc: 'Find businesses based on geographic location with advanced search filters and radius targeting.', color: 'text-cyan-400' },
    { icon: BarChart3, title: 'Custom Lead Scoring', desc: 'Automatically score and prioritize leads based on fit, intent, and engagement signals.', color: 'text-purple-400' },
    { icon: ListTodo, title: 'Lead Management', desc: 'Organize your leads with custom categories, status tracking, notes, and task management.', color: 'text-emerald-400' },
    { icon: Users, title: 'Team Collaboration', desc: 'Collaborate with your sales team with shared access, assignment features, and activity tracking.', color: 'text-pink-400' },
    { icon: Zap, title: 'Automated Enrichment', desc: 'Automatically gather business details, contact information, and social profiles for each lead.', color: 'text-amber-400' },
    { icon: LayoutDashboard, title: 'Analytics Dashboard', desc: 'Track lead conversion metrics, team performance, and sales pipeline analytics.', color: 'text-blue-400' },
  ];

  const differentiators = [
    { icon: Globe, title: 'All-in-One Platform', desc: 'No more juggling multiple tools. Discovery Hub combines search, enrichment, scoring, and CRM in a single platform.', color: 'text-cyan-400' },
    { icon: Zap, title: 'AI-Powered Insights', desc: 'Leverage artificial intelligence to surface the most promising leads and automate repetitive data tasks.', color: 'text-purple-400' },
    { icon: BarChart3, title: 'Real-Time Data', desc: 'Always work with fresh, accurate data. Our enrichment engine continuously updates lead information.', color: 'text-emerald-400' },
    { icon: ShieldCheck, title: 'Enterprise Security', desc: 'Your data is protected with enterprise-grade security, role-based access controls, and full audit trails.', color: 'text-pink-400' },
  ];

  const workflow = [
    { label: 'Search', desc: 'Find leads by location & industry' },
    { label: 'Enrich', desc: 'Auto-gather business data' },
    { label: 'Score', desc: 'AI ranks by fit & intent' },
    { label: 'Convert', desc: 'Engage & close deals' },
  ];

  const testimonials = [
    { name: 'Sarah Chen', role: 'VP of Sales, TechCorp', quote: 'Discovery Hub cut our lead research time by 70%. The automated enrichment alone is worth the investment.', rating: 5 },
    { name: 'Marcus Johnson', role: 'Sales Director, GrowthIO', quote: 'Finally a platform that combines everything we need. Our team productivity has doubled since switching.', rating: 5 },
    { name: 'Emily Rodriguez', role: 'Head of BD, ScaleUp', quote: 'The lead scoring is incredibly accurate. We\'re closing 40% more deals by focusing on the right prospects.', rating: 5 },
  ];

  const faqs = [
    { q: 'How is Discovery Hub different from other lead generation tools?', a: 'Discovery Hub combines lead generation, data enrichment, scoring, and management in one platform with a focus on B2B sales. Unlike traditional tools, we provide complete lead management from discovery to conversion.' },
    { q: 'How accurate is the lead data?', a: 'We use multiple data sources and verification methods to ensure high accuracy. Our enrichment process includes both automated and manual verification steps for critical information.' },
    { q: 'Can I import my existing leads?', a: 'Yes, Discovery Hub supports CSV imports and can integrate with popular CRM platforms. You can easily bring your existing leads into the system.' },
    { q: 'How does the lead scoring work?', a: 'Our proprietary scoring algorithm analyzes multiple factors including company demographics, engagement history, online presence, and buying intent signals to generate a comprehensive score.' },
    { q: 'How many team members can use Discovery Hub?', a: 'Different subscription plans support different team sizes. Our plans scale from solo users to enterprise teams with unlimited members.' },
  ];

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 landing-gradient-text">
            How Discovery Hub Works
          </h1>
          <p className="text-lg md:text-xl landing-text-muted max-w-3xl mx-auto mb-10">
            A comprehensive platform that streamlines lead generation, management, and conversion for B2B sales teams
          </p>
          <a href="#process" className="landing-glass inline-flex items-center gap-2 px-6 py-3 rounded-full text-white hover:bg-white/10 transition-colors">
            See How It Works
            <ArrowDown className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* 4-Step Process */}
      <section id="process" className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 landing-gradient-text">Our 4-Step Process</h2>
            <p className="landing-text-muted max-w-2xl mx-auto">
              Discovery Hub transforms the way your sales team finds and converts B2B leads with our streamlined approach
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-10">
              {steps.map((step) => (
                <div key={step.num} className="flex gap-6">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm">
                    {step.num}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                    <p className="landing-text-muted">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl overflow-hidden landing-glass border border-white/10">
              <img src="/lovable-uploads/a205c4d3-ecdc-4809-99ba-3a7777b13819.png" alt="Discovery Hub dashboard on laptop screen" className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Discovery Hub */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-8 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 landing-gradient-text">Why Choose Discovery Hub</h2>
            <p className="landing-text-muted max-w-2xl mx-auto">
              Built for modern sales teams who demand more from their tools
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {differentiators.map((d) => (
              <div key={d.title} className="landing-glass rounded-3xl p-8 landing-card-hover">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-6 ${d.color} bg-white/5`}>
                  <d.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-3">{d.title}</h3>
                <p className="landing-text-muted text-sm leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration & Workflow */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-8 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 landing-gradient-text">Seamless Workflow</h2>
            <p className="landing-text-muted max-w-2xl mx-auto">
              Discovery Hub integrates into your existing sales process with a simple, powerful pipeline
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-4">
            {workflow.map((step, i) => (
              <React.Fragment key={step.label}>
                <div className="landing-glass rounded-2xl p-6 text-center flex-1 w-full md:w-auto">
                  <div className="text-2xl font-bold landing-gradient-text mb-1">{step.label}</div>
                  <p className="landing-text-muted text-sm">{step.desc}</p>
                </div>
                {i < workflow.length - 1 && (
                  <ArrowRight className="text-white/30 w-6 h-6 flex-shrink-0 rotate-90 md:rotate-0" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-8 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 landing-gradient-text">Platform Features</h2>
            <p className="landing-text-muted max-w-3xl mx-auto">Explore the key components that make Discovery Hub the ultimate lead generation and management platform</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="landing-glass rounded-3xl p-8 landing-card-hover">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-6 ${f.color} bg-white/5`}>
                  <f.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-3">{f.title}</h3>
                <p className="landing-text-muted text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-8 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 landing-gradient-text">What Our Users Say</h2>
            <p className="landing-text-muted max-w-2xl mx-auto">
              Trusted by sales teams at fast-growing companies
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="landing-glass rounded-3xl p-8">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-white/90 mb-6 text-sm leading-relaxed italic">"{t.quote}"</p>
                <div>
                  <div className="text-white font-semibold text-sm">{t.name}</div>
                  <div className="landing-text-muted text-xs">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-8 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 landing-gradient-text">Frequently Asked Questions</h2>
            <p className="landing-text-muted">Everything you need to know about Discovery Hub</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.q} className="landing-glass rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">{faq.q}</h3>
                <p className="landing-text-muted text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-8 max-w-4xl">
          <div className="landing-glass rounded-3xl p-12 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-cyan-500/10" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                Ready to Transform Your Lead Generation?
              </h2>
              <p className="landing-text-muted max-w-2xl mx-auto mb-8 text-lg">
                Join our beta program today and see Discovery Hub in action.
              </p>
              <Link to="/auth" className="landing-btn-primary inline-flex items-center gap-2">
                Join Beta Now
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
